/* eslint-disable max-lines-per-function */
import React from 'react';
import { createState, DevTools, State, useState } from '@hookstate/core';
import { Validation as validation } from '@hookstate/validation';
import useEntryCommentState from './EntryCommentState';
import useEntryDispatchState from './EntryDispatchState';
import { useLang } from '../../../hooks/useLang';
import notify from '~/utils/notify';
import { Broadcasted } from '@hookstate/broadcasted';
import IEntry from '../interfaces/IEntry';
import IEntryState from '../interfaces/IEntryState';
import IEntryStatusActions from '../interfaces/IEntryStatusActions';
import ITliaEntry from '../interfaces/ITliaEntry';
import IRecallEntry from '../interfaces/IRecallEntry'
import { LatLng } from 'leaflet';
import { authFetch } from '~/services/fetch';
import { parseDataEntry } from '../utils/parse';
import { formatForSending } from '../utils/format';
import { validPhone } from '~/utils/validators';
import { useMarkerState } from '~/features/Map/stores/MapState';
import useSystemState from '~/features/System/stores/SystemState';

type DispatchGroupProps = {
  readonly dispatchGroupId: number | string;
  readonly loading: State<boolean>
}

interface IEntryOpenCloseOccurrence {
  readonly comment: string;
}

const entryDefault: IEntry = {
  id: null,
  name: null,
  phone: null,
  address: null,
  location: '',
  reference: null,
  crossingStreet: null,
  coordinates: null,
  latitude: null,
  longitude: null,
  entryOriginId: '',
  stateId: null,
  cityId: null,
  districtId: null,
  typeId: null,
  subtypeId: null,
  typeName: null,
  subtypeName: null,
  createdAt: null,
  updatedAt: null,
  vehicle: {
    plate: null,
    circunstanceId: null,
    externalKeyTracking: null
  },
  totalSubtypes: 0,
  saved: false,
  totalDistricts: 0,
  incidentId: null,
}

const entryStatusActionsDefault: IEntryStatusActions = {
  canCreate: true,
  canSave: false,
  hasPrev: false,
  hasNext: false,
  isOpenRecall: false,
  isInvalidRequest: false,
  canSetDispatchGroup: false,
  isOpenModalLastCalls: false,
  isOpenModalVehicle: false,
  showVehicleButton: false,
}

const suggestDispatchGroupModal = createState<{
  readonly open: boolean;
  readonly suggestedData: readonly any[] | null;
}>({
  open: false,
  suggestedData: null,
})

const entryStore = Object.create(entryDefault)

const store = createState<IEntryState>({
  entry: entryStore,
  entryStatusActions: entryStatusActionsDefault,
})

const loadingsActions = createState({
  loadingSaveEntry: false,
  loadingDispatchEntry: false,
})

const safeBike = createState({
  canSave: false
})

// eslint-disable-next-line new-cap
store.attach(Broadcasted('entry-events'));

// eslint-disable-next-line new-cap
DevTools(store).label('Entry')

export default function useEntryState() {
  const { translate } = useLang()
  const entryDefaultState = useState(store);
  const loadingsActionsEntry = useState(loadingsActions.loadingSaveEntry);
  const loadingsActionsDispatchEntry = useState(loadingsActions.loadingDispatchEntry);
  const safeBikeCanSave = useState(safeBike.canSave)
  const suggestDispatchGroupModalState = useState(suggestDispatchGroupModal);
  const { deleteMarker, markersState, forceClosePopup } = useMarkerState();
  const { loadingDispatchConfig } = useEntryDispatchState()
  const { windowManager } = useSystemState();
  const entryStatusActions = useState(entryDefaultState.entryStatusActions);
  const entry = useState(entryDefaultState.entry);
  const {
    id,
    phone,
    typeId,
    subtypeId,
    entryOriginId,
    location,
    cityId,
    vehicle,
    totalSubtypes,
    name,
    address,
    latitude,
    longitude,
    typeName,
    subtypeName,
    createdAt,
    saved,
    districtId,
    totalDistricts
  } = entry;
  const { canCreate, hasPrev, hasNext, canSave, isInvalidRequest, canSetDispatchGroup, isOpenModalVehicle, showVehicleButton } = entryStatusActions;

  const { syncPreviousComments, resetComment } = useEntryCommentState();
  const { getEntryDispatch, resetEntryDispatch, entryDispatch } = useEntryDispatchState();

  id.attach(validation);
  phone.attach(validation);
  typeId.attach(validation);
  entryOriginId.attach(validation);
  cityId.attach(validation);
  vehicle.attach(validation);
  subtypeId.attach(validation);
  districtId.attach(validation);

  validation(id).validate(() => Boolean(id.get() != null), 'Se o ID está valido');
  validation(phone).validate(() => Boolean(validPhone(phone.get())), 'Telefone Completo');
  validation(typeId).validate(() => Boolean(typeId.get() != null), 'Se o Tipo está valido');
  validation(entryOriginId).validate(() => Boolean(entryOriginId.get() != ''), 'Se a Origem da Ligação está valida');
  validation(location).validate(() => Boolean(location.get() != '' && location.get() != null), 'Se a Localização está valida');
  validation(cityId).validate(() => Boolean(cityId.get() != null), 'Se a Cidade está valida');
  validation(vehicle).validate(() => !!(showVehicleButton.get() && (vehicle.circunstanceId.get())), 'Se exige uma circunstancia para o cadastro');


  validation(subtypeId).validate(() => (totalSubtypes.get() > 0 ? totalSubtypes.get() > 0 && subtypeId.get() != null : true), 'Se precisa informar um subtipo');
  validation(districtId).validate(() => (totalDistricts.get() > 0 ? totalDistricts.get() > 0 && districtId.get() != null : true), 'Se precisa informar um bairro');
  // validation(totalSubtypes).validate(() => !(totalSubtypes.get() > 0), 'Se precisa informar um subtipo');


  const resetEntry = () => {
    entry.set(Object.create(entryDefault));
    entryStatusActions.set(Object.create(entryStatusActionsDefault));
    resetComment();
    resetEntryDispatch(); getEntryFromId
  }

  const validateActionsEntry = () => {
    if ((validation(id).valid() &&
      validation(location).valid() &&
      validation(typeId).valid() &&
      validation(entryOriginId).valid() &&
      validation(cityId).valid() &&
      (validation(subtypeId).valid()) &&
      (validation(districtId).valid())
    )) {

      // #FIXME: Avaliar melhor forma de validar este formulário.
      // if (showVehicleButton.get()) {
      //   if (!validation(vehicle).valid()) {
      //     notify({message: `${translate('Need to inform a vehicle data to proceed!')}`, type: 'error'});
      //     isInvalidRequest.set(true)
      //     return false;
      //   }
      // }


      return true
    } else {
      notify({ message: `${translate('Please check the form information and try again!')}`, type: 'error' });
      isInvalidRequest.set(true)
      return false
    }
  }

  const getMyLastEntry = async () => {
    try {
      const { data } = await authFetch({
        url: '/entry/get-my-last',
        method: 'POST'
      });

      if (data && data !== '') {
        windowManager()['VehicleModal'].open.set(false);
        entry.set(parseDataEntry(data.entry));
        hasPrev.set(data.hasPrev);
        hasNext.set(data.hasNext);
        canCreate.set(true);
        canSave.set(true);
        canSetDispatchGroup.set(true);
        isInvalidRequest.set(false);

        const dataId = data.entry.id;
        await syncPreviousComments(dataId);
        await getEntryDispatch(dataId);

        if (parseDataEntry(data.entry).typeId) {
          saved.set(true);
        }
        return true;
      }
      resetEntry();
      return false;
    } catch (error) {
      console.error('Erro ao buscar última entrada:', error);
      resetEntry();
      return false;
    }
  }

  const getEntryFromId = async (entryId: number) => {
    try {
      const { data } = await authFetch({
        url: `/entry/${entryId}`,
        method: 'GET'
      });

      if (data && data !== '') {
        entry.set(parseDataEntry(data.entry));
        hasPrev.set(data.hasPrev);
        hasNext.set(data.hasNext);
        canCreate.set(true);
        canSave.set(true);
        canSetDispatchGroup.set(true);
        isInvalidRequest.set(false);

        await syncPreviousComments(entryId);
        await getEntryDispatch(entryId);
        return true;
      }
      resetEntry();
      return false;
    } catch (error) {
      console.error('Erro ao buscar entrada por ID:', error);
      resetEntry();
      return false;
    }
  }

  return ({
    entry: () => entry,
    resetEntry: () => resetEntry,
    entryStatusActions: () => entryStatusActions,
    suggestDispatchGroupModal: () => suggestDispatchGroupModalState,
    getMyLastEntry: getMyLastEntry,
    getEntryFromId: getEntryFromId,
    loadingsActionsEntry: loadingsActionsEntry,
    loadingsActionsDispatchEntry: loadingsActionsDispatchEntry,
    safeBikeCanSave: safeBikeCanSave,
    createEntry: async () => {
      try {
        const { data } = await authFetch({
          url: '/entry/store',
          method: 'POST'
        });

        entry.set(parseDataEntry(data.entry));
        hasPrev.set(data.hasPrev);
        hasNext.set(false);
        canSave.set(true);
        canSetDispatchGroup.set(true);

        const dataId = data.entry.id;
        await syncPreviousComments(dataId);
        await getEntryDispatch(dataId);

        return true;
      } catch (error) {
        console.error('Erro ao criar entrada:', error);
        notify({
          message: `${translate('Error creating entry. Please try again.')}`,
          type: 'error'
        });
        return false;
      }
    },
    previousEntry: async () => {
      try {
        const { data } = await authFetch({
          url: '/entry/get-my-previous',
          method: 'POST',
          data: {
            'idRef': id.get()
          }
        });

        const parsed = parseDataEntry(data.entry);
        windowManager()['VehicleModal'].open.set(false);
        entry.set(parsed);
        hasPrev.set(data.hasPrev);
        hasNext.set(data.hasNext);

        const dataId = data.entry.id;
        await syncPreviousComments(dataId);
        await getEntryDispatch(dataId);
      } catch (error) {
        console.error('Erro ao buscar entrada anterior:', error);
        notify({
          message: `${translate('Error loading previous entry.')}`,
          type: 'error'
        });
      }
    },
    nextEntry: async () => {
      try {
        const { data } = await authFetch({
          url: '/entry/get-my-next',
          method: 'POST',
          data: {
            'idRef': id.get()
          }
        });

        windowManager()['VehicleModal'].open.set(false);
        entry.set(parseDataEntry(data.entry));
        hasPrev.set(data.hasPrev);
        hasNext.set(data.hasNext);

        const dataId = data.entry.id;
        await syncPreviousComments(dataId);
        await getEntryDispatch(dataId);
      } catch (error) {
        console.error('Erro ao buscar próxima entrada:', error);
        notify({
          message: `${translate('Error loading next entry.')}`,
          type: 'error'
        });
      }
    },
    saveEntry: async () => {
      if (!validateActionsEntry()) {
        return false;
      }

      loadingDispatchConfig.set(true);
      try {
        const response = await authFetch({
          url: `/entry/${id.get()}`,
          method: 'PUT',
          data: formatForSending(entry.get())
        });

        isInvalidRequest.set(false);
        notify({
          message: `${translate('Saved successfully!')}`,
          type: 'success'
        });

        await new Promise(resolve => setTimeout(resolve, 1000));
        await getEntryDispatch(id.get());

        const hasDispatchConfig = entryDispatch().get().length > 0;
        
        if (!hasDispatchConfig) {
          try {
            const { data } = await authFetch({
              url: '/entry/suggested-agencies',
              method: 'POST',
              data: {
                type_id: typeId.get(),
                subtype_id: subtypeId.get(),
              },
            });
            
            if (data && Array.isArray(data) && data.length > 0) {
              suggestDispatchGroupModal.suggestedData.set(data);
              suggestDispatchGroupModal.open.set(true);
              return 'MISSING_DISPATCH_CONFIG';
            }
          } catch (error) {
            console.error('Erro ao verificar agências sugeridas:', error);
          }
        }

        entry.saved.set(true);
        return response.status;
      } catch (error) {
        console.error('Erro ao salvar entrada:', error);
        notify({
          message: `${translate('Error saving entry. Please try again.')}`,
          type: 'error'
        });
        return false;
      } finally {
        loadingDispatchConfig.set(false);
      }
    },
    handleUpdateMyEntries: async () => {
      const response = await authFetch({
        url: '/entry/get-my-entries',
      })

      return response.data

    },
    setDispatchGroup: async ({ dispatchGroupId }: DispatchGroupProps) => {
      try {
        await authFetch({
          url: `/entry/set-dispatch-group`,
          method: 'POST',
          data: {
            'entry_id': id.get(),
            'dispatch_group_id': dispatchGroupId
          }
        });

        isInvalidRequest.set(false);
        notify({
          message: `${translate('Group successfully add')}`,
          type: 'success'
        });
        await getEntryDispatch(id.get());
        return true;
      } catch (error) {
        console.error('Erro ao definir grupo de despacho:', error);
        notify({
          message: `${translate('Error setting dispatch group.')}`,
          type: 'error'
        });
        return false;
      }
     
    },
    tliaEntry: async (param: ITliaEntry) => {
      try {
        await authFetch({
          url: `/entry/discard`,
          method: 'POST',
          data: {
            'entrieId': id.get(),
            'comment': param.reason,
            'entryStatusId': param.entryStatusId,
            'name': param.name,
            'phone': param.phone,
            'address_reference': param.addressReference,
            'location': param.location,
            'latitude': param.latitude,
            'longitude': param.longitude,
            'entry_origin_id': param.entryOriginId,
            'city_id': param.cityId,
            'district_id': param.districtId,
            'type_id': param.typeId,
            'subtype_id': param.subtypeId,
            'incident_id': param.incidentId
          }
        });

        notify({
          message: `${translate('Finished successfully!')}`,
          type: 'success'
        });

        forceClosePopup.set(!forceClosePopup.get());
        markersState()['occurrence'].set({});
        deleteMarker({ markerType: 'entry', markerIdentifier: id.get() });
        deleteMarker({ markerType: 'locationRequester', markerIdentifier: entry.incidentId.get() });
        await getMyLastEntry();
      } catch (error) {
        console.error('Erro ao descartar entrada:', error);
        notify({
          message: `${translate('Error discarding entry.')}`,
          type: 'error'
        });
      }
    },
    dispatchEntry: async () => {
      if (validateActionsEntry()) {
        loadingsActionsDispatchEntry.set(true)
        const response = await authFetch({
          url: `/entry/dispatch`,
          method: 'POST',
          data: {
            'entrieId': id.get()
          }
        })
        markersState()['occurrence'].set({});
        deleteMarker({ markerType: 'entry', markerIdentifier: id.get() })
        deleteMarker({ markerType: 'locationRequester', markerIdentifier: entry.incidentId.get() })
        return response.data

      }
    },
    entryOpenCloseOccurrence: async ({ comment }: IEntryOpenCloseOccurrence) => {
      if (validateActionsEntry()) {
        const response = await authFetch({
          url: '/entry/create/closed-occurrence',
          method: 'POST',
          data: {
            entry_id: id.get(),
            comment: comment,
          }
        })
        markersState()['occurrence'].set({});
        deleteMarker({ markerType: 'entry', markerIdentifier: id.get() })
        deleteMarker({ markerType: 'locationRequester', markerIdentifier: entry.incidentId.get() })
        return response
      }
    },
    recallEntry: async (param: IRecallEntry) => {
      const response = await authFetch({
        url: `/entry/recall`,
        method: 'POST',
        data: {
          entrieId: id.get(),
          entryStatusId: 2,
          occurrenceId: param.occurrenceId,
          name: param.name,
          phone: param.phone,
          comment: param.comment,
          entry_origin_id: param.entryOriginId
        }
      })
      if (response.status === 200) {
        markersState()['occurrence'].set({});
        deleteMarker({ markerType: 'entry', markerIdentifier: id.get() })
        deleteMarker({ markerType: 'locationRequester', markerIdentifier: entry.incidentId.get() })
        getMyLastEntry().then();
      }

      return response
    },
    socketUpdateEntry: (entryParams) => {
      if (entryParams && entryParams.id == id.get()) {
        id.set(entryParams.id);
        name.set(entryParams.name);
        phone.set(entryParams.name);
        address.set(entryParams.address);
        location.set(entryParams.location);
        latitude.set(entryParams.latitude);
        longitude.set(entryParams.longitude);
        typeId.set(entryParams.typeId);
        subtypeId.set(entryParams.subtype_id);
        entryOriginId.set(entryParams.entry_origin_id);
        typeName.set(entryParams.typename);
        subtypeName.set(entryParams.subtypeName)
      }
    },
    validateActionsEntry: validateActionsEntry,
  });
}


